import { useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLingui } from "@lingui/react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { createPhoneCountryCodeOptions, getDialCodeByCountryCode } from "@/utils/referenceData";
import type { FieldConfig } from "../types";

interface PhoneFieldProps<T> {
  field: FieldConfig<T>;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
}

const phoneCountryOptions = createPhoneCountryCodeOptions();

export function PhoneField<T>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder,
  "aria-describedby": ariaDescribedBy,
}: PhoneFieldProps<T>) {
  const { i18n } = useLingui();
  const { setValue, watch } = useFormContext();
  const countryCodeFieldName = field.countryCodeFieldName as string | undefined;
  const countryCode = countryCodeFieldName ? watch(countryCodeFieldName) : undefined;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dialCode = getDialCodeByCountryCode(countryCode);

  const suggestedOptions = useMemo(
    () => phoneCountryOptions.filter((opt) => opt.suggested),
    []
  );

  const regularOptions = useMemo(
    () => phoneCountryOptions.filter((opt) => !opt.suggested),
    []
  );

  const filteredSuggested = useMemo(
    () =>
      search
        ? suggestedOptions.filter(
            (opt) =>
              opt.label.toLowerCase().includes(search.toLowerCase()) ||
              opt.value.toString().toLowerCase().includes(search.toLowerCase())
          )
        : suggestedOptions,
    [suggestedOptions, search]
  );

  const filteredRegular = useMemo(
    () =>
      search
        ? regularOptions.filter(
            (opt) =>
              opt.label.toLowerCase().includes(search.toLowerCase()) ||
              opt.value.toString().toLowerCase().includes(search.toLowerCase())
          )
        : regularOptions,
    [regularOptions, search]
  );

  const handleSelectCountry = (code: string) => {
    if (countryCodeFieldName) {
      setValue(countryCodeFieldName, code, { shouldDirty: true });
    }
    setOpen(false);
    setSearch("");
  };

  const selectedOption = countryCode
    ? phoneCountryOptions.find((opt) => opt.value === countryCode)
    : undefined;

  return (
    <div className="@container/phone flex gap-0 w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={i18n._({
              id: "changeRequest.employeeData.field.phoneCountryCode",
              message: "Phone country code",
            })}
            disabled={disabled}
            className="shrink-0 rounded-r-none border-r-0 justify-between gap-1 px-2 font-normal"
            type="button"
          >
            <span className="flex items-center gap-1 text-sm">
              {selectedOption ? (
                <>
                  <span className="hidden @[240px]/phone:inline">
                    {selectedOption.emoji}
                  </span>
                  <span>{dialCode}</span>
                </>
              ) : (
                <span className="text-muted-foreground">+...</span>
              )}
            </span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-50" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <div className="p-2">
            <Input
              placeholder={i18n._({ id: "common.multiselect.searchPlaceholder", message: "Search..." })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
              autoFocus
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {filteredSuggested.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent cursor-pointer ${
                  opt.value === countryCode ? "bg-accent" : ""
                }`}
                onClick={() => handleSelectCountry(String(opt.value))}
              >
                {opt.label}
              </button>
            ))}
            {filteredSuggested.length > 0 && filteredRegular.length > 0 && (
              <div className="border-t my-1" />
            )}
            {filteredRegular.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent cursor-pointer ${
                  opt.value === countryCode ? "bg-accent" : ""
                }`}
                onClick={() => handleSelectCountry(String(opt.value))}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-required={field.required || undefined}
        autoComplete={field.autoComplete}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="rounded-l-none flex-1 min-w-0"
      />
    </div>
  );
}
